/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-01-22 18:49:32
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-10-29 14:51:52
 * @FilePath: /src/components/Layout/HeaderV2/index.tsx
 * @Description:
 */
'use client';
import { getSearchKeywords } from '@/api/public';
import type { DingTongConfig } from '@/api/types/ding-tong';
import { ArrayElement, GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { getCartSitePolicy } from '@/domain/cart/experiment';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { checkHeaderConfig } from '@/utils/tools';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchStore } from './components/SearchComponent/store/search-store';
import {
  loadHotSearchProducts,
  loadSearchConfig,
} from './components/SearchComponent/loadSearchData';
import HeaderMobile from './Header.m';
import HeaderPC from './Header.pc';

export interface HeaderProps {
  logoData: ArrayElement<GetResponseType<ApiResponseDefinition['POST:/api/block/block']>['list']>;
  dtInfo: DingTongConfig | null;
  quickEntryData: ArrayElement<
    GetResponseType<ApiResponseDefinition['POST:/api/block/block']>['list']
  >;
  headerNavList?: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  deviceType: EPlatform;
  isBot?: boolean;
}

const ZeeloolHeader = (props: HeaderProps) => {
  const canBuyCartsList = useStore((state) => state.canBuyCartsList);
  const [cartProductNum, setCartProductNum] = useState<string | number>(0);
  const setIsShowHeaderNav = useStore((state) => state.setIsShowHeaderNav);
  const setIsHeaderDarkMode = useStore((state) => state.setIsHeaderDarkMode);
  const isShowDtBannerByPage = useStore((state) => state.isShowDtBannerByPage);
  const setIsShowDtBannerByPage = useStore((state) => state.setIsShowDtBannerByPage);
  const isShowHeader = useStore((state) => state.isShowHeaderNav);
  const isDarkMode = useStore((state) => state.isHeaderDarkMode);
  const { pathname } = useRouteMetadata();
  const isShowAppDownloadBanner = useStore((state) => state.isShowAppDownloadBanner);
  const isHomeHeaderCollapsed = useStore((state) => state.isHomeHeaderCollapsed);
  const setIsShowAppDownloadBanner = useStore((state) => state.setIsShowAppDownloadBanner);
  const isMobileDevice = props.deviceType !== EPlatform.WEB;
  const isClientInit = useRef(false);
  const isWapCart =
    props.deviceType === EPlatform.WAP && (pathname === '/cart' || pathname.startsWith('/cart/'));
  const showV1CartNavigation = useStore(
    (state) =>
      isWapCart &&
      getCartSitePolicy(env.NEXT_PUBLIC_SITE_NAME, state.cartExperiment).experience === 'v1',
  );

  const headerConfig = useMemo(() => {
    const c = checkHeaderConfig(pathname, isMobileDevice);
    if (props.deviceType === EPlatform.IOS || props.deviceType === EPlatform.ANDROID) {
      return { ...c, isShowHeaderNav: false };
    }
    // V1 2.1.1：新版 WAP 保留导航、搜索和 Logo；US 消费真实分组，小语种用固定策略，
    // 沿用全局 Header，不重复请求实验，也不带入顶通营销横幅。
    if (showV1CartNavigation) {
      return { ...c, isShowHeaderNav: true, isShowDtBanner: false };
    }
    return c;
  }, [pathname, isMobileDevice, props.deviceType, showV1CartNavigation]);

  // 搜索相关的store
  const setKeywordsMaps = useSearchStore((state) => state.setKeywordsMaps);
  const setSearchHistory = useSearchStore((state) => state.setSearchHistory);

  // 自动补全独立加载，不阻塞热搜配置或商品请求。
  const initSearchKeywords = async () => {
    try {
      // 1. 获取搜索自动补全数据（轻量）
      const keywordsResponse = await getSearchKeywords();
      if (keywordsResponse && keywordsResponse.code === 200) {
        const newKeywordsMaps: any[] = [];

        keywordsResponse.data.attributes.forEach((item: string) => {
          newKeywordsMaps.push({
            is_attr: '1',
            key: item.toLowerCase(),
            label: item,
          });
        });

        keywordsResponse.data.products.forEach((item: any) => {
          newKeywordsMaps.push({
            is_product: '1',
            key: item.name.toLowerCase(),
            value: item.shop_sku,
            label: item.name,
          });
        });

        setKeywordsMaps(newKeywordsMaps);
      }
    } catch (error) {
      logger.error('Failed to initialize search keywords:', error);
    }
  };

  // 按需加载商品数据（重量数据）
  const loadSearchDataIfNeeded = useCallback(async () => {
    try {
      await loadHotSearchProducts();
    } catch (error) {
      logger.error('Failed to load search data:', error);
    }
  }, []);

  useEffect(() => {
    isClientInit.current = true;
    const searchHistoryCache = localStorage.getItem('zl-searchHistory');
    if (searchHistoryCache) {
      setSearchHistory(JSON.parse(searchHistoryCache) as string[]);
    }
    // 初始化轻量数据（热词关键词），商品数据延迟加载
    initSearchKeywords();
    void loadSearchConfig().catch((error) => {
      logger.error('Failed to initialize search configuration:', error);
    });
  }, []);

  useEffect(() => {
    const qtyArr: number[] = [];
    if (canBuyCartsList && canBuyCartsList.length) {
      canBuyCartsList.forEach((item) => {
        (item as { carts: Array<{ qty: number }> }).carts.forEach((items) => {
          qtyArr.push(items.qty);
        });
      });
    }
    let sum: number | string = 0;
    qtyArr.forEach((i) => {
      (sum as number) += i;
    });
    if (sum > 99) {
      sum = '...';
    }
    setCartProductNum(sum);
  }, [canBuyCartsList]);

  useEffect(() => {
    const {
      isShowHeaderNav,
      isHeaderDarkMode,
      isShowDtBanner,
      isShowAppDownloadBanner: issp,
    } = headerConfig;

    setIsShowHeaderNav(isShowHeaderNav);
    setIsHeaderDarkMode(isHeaderDarkMode);
    setIsShowDtBannerByPage(isShowDtBanner);
    setIsShowAppDownloadBanner(issp);
  }, [
    headerConfig,
    setIsShowHeaderNav,
    setIsHeaderDarkMode,
    setIsShowDtBannerByPage,
    setIsShowAppDownloadBanner,
  ]);
  const finalHeaderConfig = useMemo(() => {
    // WAP 购物车直接使用当前路由/分组配置，避免路由切换或分组晚返回时
    // 读到尚未由 effect 同步的全局显隐状态，误展示旧组导航或顶通。
    if (isClientInit.current && !isWapCart) {
      return {
        isShowHeader: isShowHeader,
        isDarkMode,
        isShowDtBannerByPage,
        // 路由配置会重置展示开关；首页后退仍应保持离开前的折叠状态。
        isShowAppDownloadBanner:
          isShowAppDownloadBanner && (pathname !== '/' || !isHomeHeaderCollapsed),
      };
    }
    return {
      isShowHeader: headerConfig.isShowHeaderNav,
      isDarkMode: headerConfig.isHeaderDarkMode,
      isShowDtBannerByPage: headerConfig.isShowDtBanner,
      isShowAppDownloadBanner: headerConfig.isShowAppDownloadBanner,
    };
  }, [
    isShowHeader,
    isDarkMode,
    isShowDtBannerByPage,
    isShowAppDownloadBanner,
    headerConfig,
    pathname,
    isHomeHeaderCollapsed,
    isWapCart,
  ]);

  const finalProps = Object.assign({}, props, finalHeaderConfig);
  return (
    <>
      {props.deviceType === EPlatform.WAP && (
        <HeaderMobile
          {...finalProps}
          cartProductNum={cartProductNum}
          deviceType={props.deviceType}
          loadSearchDataIfNeeded={loadSearchDataIfNeeded}
          isBot={props.isBot}
        />
      )}
      {props.deviceType === EPlatform.WEB && (
        <HeaderPC
          {...finalProps}
          cartProductNum={cartProductNum}
          loadSearchDataIfNeeded={loadSearchDataIfNeeded}
          isBot={props.isBot}
        />
      )}
    </>
  );
};

export default ZeeloolHeader;
